import React, { useCallback, useState } from "react";
import styled from "styled-components";
import OutsideClickHandler from "react-outside-click-handler";
import { AiFillCaretDown, AiFillCaretUp } from "react-icons/ai";
import { BiAddToQueue } from "react-icons/bi";
import { RiFileExcel2Line } from "react-icons/ri";
import { VscJson } from "react-icons/vsc";

import { ImportExportType } from "features/shared/types/importExport";
import MenuButton from "./menuButton";
import { useDispatch } from "services/redux/rootReducer";
import { EditorAction } from "features/shared/types/actions.editor";
import { ModalName } from "features/shared/types/modal";
interface Props {
    startText: string;
    type: "import" | "export";
    options: ImportExportType[]; // first element will be shown on button
    onClickHandler?: () => void;
}

const ImportExportButton: React.FC<Props> = ({
    startText,
    type,
    options,
    onClickHandler,
}) => {
    const [isSelectShown, setShown] = useState<boolean>(false);

    const dispatch = useDispatch();

    const handleSelect = useCallback(
        (
            e: React.MouseEvent<HTMLDivElement>,
            importExportType: ImportExportType
        ) => {
            e.stopPropagation();
            if (type === "import") {
                dispatch({
                    type: EditorAction.SET_IMPORT_TYPE,
                    importType: importExportType,
                });
            } else {
                dispatch({
                    type: EditorAction.SET_EXPORT_TYPE,
                    exportType: importExportType,
                });
            }
            if (onClickHandler) {
                onClickHandler();
            } else {
                dispatch({
                    type: EditorAction.TOGGLE_MODAL,
                    modal: {
                        name: ModalName.ADD,
                    },
                });
            }
            setShown(false);
        },
        [dispatch, onClickHandler, type]
    );

    const getIcon = useCallback((type: ImportExportType) => {
        let displayedIcon;

        switch (type) {
            case ImportExportType.EXCEL:
                displayedIcon = RiFileExcel2Line;
                break;
            case ImportExportType.JSON:
                displayedIcon = VscJson;
                break;
            case ImportExportType.FORM:
                displayedIcon = BiAddToQueue;
        }

        return displayedIcon;
    }, []);

    const dropdownDisplay = React.useMemo(() => {
        return options
            .slice(1)
            .map((option) => (
                <MenuButton
                    text={`${startText}`}
                    key={option}
                    icon={getIcon(option)}
                    onClickHandler={(e) => handleSelect(e, option)}
                />
            ));
    }, [options, startText, getIcon, handleSelect]);

    return (
        <OutsideClickHandler
            onOutsideClick={() => isSelectShown && setShown(!isSelectShown)}
        >
            <Container>
                <SelectBar
                    onClick={() => options.length && setShown(!isSelectShown)}
                >
                    <MenuButton
                        text={`${startText}`}
                        key={options[0]}
                        icon={getIcon(options[0])}
                        onClickHandler={(e) => handleSelect(e, options[0])}
                    />
                    <div>
                        {isSelectShown ? (
                            <AiFillCaretUp />
                        ) : (
                            <AiFillCaretDown />
                        )}
                    </div>
                </SelectBar>
                {isSelectShown && <Dropdown>{dropdownDisplay}</Dropdown>}
            </Container>
        </OutsideClickHandler>
    );
};

const Container = styled.div`
    height: 2.6rem;
    width: fit-content;
    position: relative;

    box-shadow: 1px 0px 10px 1px rgba(231, 231, 231, 0.5);
`;

const SelectBar = styled.div`
    display: flex;

    color: ${(props) => props.theme.colors.neutral[600]};
    background: white;

    border-radius: 0.2rem;

    height: inherit;

    & > * {
        &:first-child {
            width: fit-content;
            box-shadow: none;
            border-right: solid 1px
                ${(props) => props.theme.colors.neutral[145]};
            border-radius: 0.2rem 0 0 0.2rem;
        }
        &:last-child {
            width: 1.8rem;
            display: grid;
            place-items: center;

            font-size: 1rem;
            color: ${(props) => props.theme.colors.primary[100]};
        }
    }

    cursor: pointer;
`;

const Dropdown = styled.div`
    position: fixed;
    z-index: 2;
    width: fit-content;

    display: flex;
    flex-flow: column;
`;

export default ImportExportButton;
